import url from 'url';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import noop from 'lodash/noop';
import isString from 'lodash/isString';
import classNames from 'clsx';

import RecentSearchesContext from 'src/common/contexts/RecentSearchesContext';
import getGuideItemPathname from 'src/common/utils/guideItem/getGuideItemPathname';
import { selectIsMobile } from 'src/common/selectors/app';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { selectGuideItem } from 'src/common/selectors/profiles';
import { closeNowPlayingDialog } from '../../../actions/dialog';
import { SponsoredRollupContext } from '../../ads/SponsoredRollupProvider';
import isPlayable from '../../../utils/guideItem/isPlayable';
import css from './guide-item.module.scss';

/**
 * TODO:
 * Eventually, this should reference GuideItemLinkWithLogger, so we can have more universal
 * logging of inappropriate links.When switching to using GuideItemLinkWithLogger, we can
 * imagine the `guideItemLink` prop will be something like the following:
 *
 *  guideItemLink = (logger) => {
 *    const guideItemUrl = getGuideItemPathnameWithLogger(guideItem, parentGuideItem)(logger);
 *    if (guideItemUrl) {
 *      return {
 *        ...url.parse(guideItemUrl),
 *        state: {
 *          itemToken: get(guideItem, 'context.token')
 *        },
 *      };
 *    }
 *  return guideItemUrl;
 * }
 */

function updateLocation(to, updater) {
  return updater ? updater(to) : to;
}

function GuideItemLink({
  id,
  guideItem,
  children,
  style,
  className,
  onClick,
  dataTestId,
  parentGuideItem,
  isMobile,
  actions,
  isNowPlayingOpen,
}) {
  const onSearchClick = useContext(RecentSearchesContext);
  const guideItemUrl = getGuideItemPathname(guideItem, parentGuideItem);
  const augmentGuideItemLink = useContext(SponsoredRollupContext);

  const handleClick = (event, to = null) => {
    onClick(to, event);
    onSearchClick();
    if (isMobile && isNowPlayingOpen && !isPlayable(guideItem)) {
      actions.closeNowPlayingDialog();
    }
  };

  if (isString(guideItemUrl)) {
    const location = {
      ...url.parse(guideItemUrl),
      state: { itemToken: get(guideItem, 'context.token') },
    };

    // augmentGuideItemLink is injected by the SponsoredRollupProvider in order
    // to add the categoryId to the location state. See SponsoredRollupProvider
    // for more details.
    const to = updateLocation(location, augmentGuideItemLink);

    return (
      <Link
        data-testid={dataTestId}
        to={to}
        style={style}
        className={classNames([className, css.guideItemLink])}
        id={id}
        onClick={(event) => handleClick(event, to)}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      data-testid={dataTestId}
      style={style}
      className={className}
      onClick={(event) => handleClick(event)}
    >
      {children}
    </div>
  );
}

GuideItemLink.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  guideItem: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  dataTestId: PropTypes.string,
  parentGuideItem: PropTypes.object,
  actions: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isNowPlayingOpen: PropTypes.bool.isRequired,
};

GuideItemLink.defaultProps = {
  onClick: noop,
  isPlayable: false,
};

// Need to add the parentGuideItem to the GuideItemLink props so that getGuideItemPathname
// has access to the additional SEO destination info that it needs to properly build SEO urls.
function mapStateToProps(state, ownProps) {
  const parentGuideId = get(
    ownProps,
    'guideItem.properties.parentProgram.destinationInfo.id',
  );
  return {
    parentGuideItem: selectGuideItem(state, parentGuideId),
    isMobile: selectIsMobile(state),
    isNowPlayingOpen: selectIsNowPlayingDialogOpen(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        closeNowPlayingDialog,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GuideItemLink);
